import { QueryResponse } from "~/modules/filters/types";
import {
    TimeSeriesItem,
    TimeSeriesUnits,
} from "~/modules/token_timeseries/types";
import { MEASURE_IDS } from "~/consts";
import { RWA_COLORS } from "~/styles/colors";

// // // //

export interface PlatformSnapshot {
    timestamp: string;
    date: string;
    measure_id: number;
    measure_value: number;
    year_of_date: number;
    month_of_date: number;
    day_in_month: number;
    day_in_week: number;
}

export const PLATFORM_SNAPSHOT_MEASURE_ID_TO_NAME: Record<number, string> = {
    [MEASURE_IDS.TotalContracts]: "Trade Count",
    [MEASURE_IDS.TotalCollateralizedFunds]: "Par Value",
    [MEASURE_IDS.TotalIssuedFunds]: "Turnover",
};

export const PLATFORM_SNAPSHOT_MEASURE_ID_COLOR_HEX: Record<number, string> = {
    [MEASURE_IDS.TotalContracts]: RWA_COLORS.emerald["600"],
    [MEASURE_IDS.TotalCollateralizedFunds]: RWA_COLORS.chart["golden-grass"],
    [MEASURE_IDS.TotalIssuedFunds]: RWA_COLORS.chart["blue-ribbon"],
};

export const PLATFORM_SNAPSHOT_MEASURE_ID_TO_UNIT: Record<
    number,
    TimeSeriesUnits
> = {
    [MEASURE_IDS.TotalContracts]: TimeSeriesUnits.token, // ENHANCEMENT - this should be `count`, ideally
    [MEASURE_IDS.TotalCollateralizedFunds]: TimeSeriesUnits.dollar,
    [MEASURE_IDS.TotalIssuedFunds]: TimeSeriesUnits.dollar,
};

/**
 * Transforms PlatformSnapshot array into QueryResponse<TimeSeriesItem>
 * Groups snapshots by measure_id and creates time series data
 */
export function transformPlatformSnapshotsToTimeSeries(
    snapshots: PlatformSnapshot[],
): QueryResponse<TimeSeriesItem> {
    // Group snapshots by measure_id
    const groupedSnapshots = snapshots.reduce(
        (acc, snapshot) => {
            const measureId = snapshot.measure_id;
            if (!acc[measureId]) {
                acc[measureId] = [];
            }
            acc[measureId].push(snapshot);
            return acc;
        },
        {} as Record<number, PlatformSnapshot[]>,
    );

    // Transform each group into TimeSeriesItem
    const results: TimeSeriesItem[] = Object.entries(groupedSnapshots).map(
        ([measureIdStr, groupSnapshots]) => {
            const measureId = parseInt(measureIdStr, 10);

            // Sort snapshots by timestamp for consistent ordering
            const sortedSnapshots = groupSnapshots.sort(
                (a, b) =>
                    new Date(a.timestamp).getTime() -
                    new Date(b.timestamp).getTime(),
            );

            // Create points as [timestamp, value] tuples
            const points = sortedSnapshots.map(
                (snapshot) =>
                    [
                        new Date(snapshot.timestamp)
                            .toISOString()
                            .split("T")[0],
                        snapshot.measure_value,
                    ] as [string, number],
            );

            const timeSeriesItem: TimeSeriesItem = {
                measure: {
                    id: measureId,
                    slug: `measure_${measureId}`,
                    name:
                        PLATFORM_SNAPSHOT_MEASURE_ID_TO_NAME[measureId] ||
                        `Measure ${measureId}`,
                    unit:
                        PLATFORM_SNAPSHOT_MEASURE_ID_TO_UNIT[measureId] ||
                        TimeSeriesUnits.dollar,
                },
                group: {
                    id: measureId,
                    type: "measure",
                    name:
                        PLATFORM_SNAPSHOT_MEASURE_ID_TO_NAME[measureId] ||
                        `Measure ${measureId}`,
                    slug: `measure_${measureId}`,
                    color:
                        PLATFORM_SNAPSHOT_MEASURE_ID_COLOR_HEX[measureId] ||
                        "#000000",
                },
                points,
            };

            return timeSeriesItem;
        },
    );

    // Create QueryResponse with basic metadata
    const response: QueryResponse<TimeSeriesItem> = {
        results,
        sort: {
            field: "timestamp",
            direction: "asc",
        },
        pagination: {
            page: 1,
            perPage: results.length,
            pageCount: 1,
            resultCount: results.length,
        },
    };

    return response;
}
